import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Set } from 'immutable';
import { EventListStore } from '../service/event-list.store';
import { OnyxSummaryEvent } from '../models/event.summary';
import { MenuStore } from '../../menus/service/menu.store';
import { EnvironmentConfiguration } from '../../environment/services/environment.configuration';
import { EventSearchParametersStore } from '../service/event-search-parameters.store';
import { EventSearchFilter } from '../models/event-filter';
import { LogLevel, LogMessage, LogService } from '../../shared/log/log.service';
import { EventFilterType } from '../models/event-filter-type-enum';
import { EventFilterUtilsService } from '../service/event-filter-util-service';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { EventSearchFilterValidator } from '../../menus/event-search-filter/event-search-filter.validator';
import { FormGroup } from '@angular/forms';
import { EventSearchFormsStore } from '../service/event-search-forms.store';
import { EventSearchText } from '../models/event-search-text';
import { EventSearchParamsDesc } from '../models/event-search-params';
import { TranslateService } from '@ngx-translate/core';
import { Page } from '../../shared/models/page';
import { PagesizeType } from '../service/pagination/pagesize-type';
import { UserLocalStorageService } from '../../permission/services/user.local.storage.service';
import { MultiAgencyService } from 'app/shared/multi-agency/multi-agency.service';
import { MultiAgency } from 'app/shared/models/multi-agency';
import { EventStore } from '../service/event.store';
import { Subscription } from 'rxjs/Subscription';
import { BulkActionPopupComponent } from '../bulk-action/bulk-action-popup/bulk-action-popup.component';
import { BulkAction } from '../models/bulk-action';
import { ActivatedRoute } from '@angular/router';
import { SpinnerService } from 'app/shared/spinner.service';

@Component({
  selector: 'pioneer-event-list',
  templateUrl: './event-list.component.html',
  styleUrls: ['./event-list.component.css']
})
export class EventListComponent implements OnInit, OnDestroy {

  public eventSearchTextForm: FormGroup;

  pagesizeType = PagesizeType.EVENTS;
  defaultAgency$: Observable<MultiAgency> = this.multiAgencyService.defaultAgency$;
  elementsPerPage = [10, 25, 50];
  agencySubscription$: Subscription = new Subscription();
  eventList = {
    original: null,
    filtered: null,
    firstLoading: true,
    isUserSearching: {
      searching: false,
      resetTable: false
    }
  };

  showEventListControl = false;

  @ViewChild(BulkActionPopupComponent)
  public bulkActionPopup: BulkActionPopupComponent;

  public eventSubs = new Subscription();

  constructor(private eventListStore: EventListStore,
              private environment: EnvironmentConfiguration,
              private menuStore: MenuStore,
              private logger: LogService,
              private flashMessagesService: FlashMessagesService,
              private eventSearchParametersStore: EventSearchParametersStore,
              private eventFilterUtils: EventFilterUtilsService,
              private eventSearchFormsStore: EventSearchFormsStore,
              private translateService: TranslateService,
              private userLocalStorageService: UserLocalStorageService,
              private multiAgencyService: MultiAgencyService,
              private activatedRoute: ActivatedRoute,
              private eventStore: EventStore,
              private spinnerService: SpinnerService) {

    this.activatedRoute.data.subscribe((resource) => {
      if (!this.eventList.firstLoading && resource['eventListData'] && resource['eventListData'].status) {
        this.resetTable();
        this.loadEvenList(0);
      }
    });
  }

  ngOnInit() {
    this.menuStore.setPageHeader('EVENTS.TITLE', false);
    this.eventSearchFormsStore.populateSearchTextForm('');
    this.eventSearchTextForm = this.eventSearchFormsStore.searchTextForm;
    this.eventStore.resetSelectedEvent();
    this.createSubscriptions();
  }

  ngOnDestroy() {
    this.agencySubscription$.unsubscribe();
    this.eventSubs.unsubscribe();
  }

  private createSubscriptions() {
    this.loadEvenList(0);

    this.eventSubs.add(this.defaultAgency$.subscribe(() => {
      if (!this.eventList.firstLoading) {
        this.eventList.firstLoading = true;
        this.loadEvenList(0);
      }
    }));

    this.eventSubs.add(this.eventListStore.events.subscribe(ev => {
      this.eventList.original = ev;
      this.eventList.filtered = ev;
      this.resetTable(false);
    }));
  }

  resetSearch(): void {
    this.eventSearchParametersStore.reset(true);
    this.eventSearchFormsStore.populateSearchTextForm('');
  }

  addSearchText(models: any): void {
    const eventSearchText = new EventSearchText(models);
    this.logger.info(`addSearchText: ${JSON.stringify(models)}`);

    if (!eventSearchText.searchText) {
      return;
    }

    const filterErrors = this.eventSearchFormsStore.filterMenuErrorsSnapshot;

    if (filterErrors != null) {
      if (filterErrors[EventSearchFilterValidator.DATE_ERROR]) {
        this.flashMessagesService.error('MESSAGES.ERROR.INVALID_DATE_RANGE');
      }


      if (filterErrors[EventSearchFilterValidator.PAYMENT_STATUS_ERROR]) {
        this.flashMessagesService.error('MESSAGES.ERROR.NO_PAYMENT_STATUS_SELECTED');
      }

      return;
    }
    this.eventSearchFormsStore.searchFromSearchText(eventSearchText);
  }

  addSearchFilter(filterField: string, filterValue: string): void {
    this.eventSearchParametersStore.addSearchFilter(filterField, filterValue, true);
  }

  removeSearchText(event: Event): void {
    const element: Element = <Element>(event.target || event.srcElement);
    const searchText = element.innerHTML.trim();
    this.eventSearchParametersStore.removeSearchText(searchText, true);
  }

  removeSearchFilter(event: Event, filter: EventSearchFilter): void {
    this.eventSearchParametersStore.removeSearchFilter(filter, true);
    const element: Element = <Element>(event.target || event.srcElement);
    element.innerHTML.trim();
  }

  loadEvenList(page: number) {
    this.loadingRequestData();
    this.onRefreshList(page).take(1).catch(error => {
      this.loadingRequestData(false);
      this.cleanTable();
      return Observable.throw(error);
    }).subscribe(ev => {
      this.loadingRequestData(false);
      this.eventList.firstLoading = false;
    });
  }

  onRefreshList(page: number) {
    return this.eventListStore.loadEvents(page,
           this.userLocalStorageService.getPagesize(PagesizeType.EVENTS),
           this.eventList.firstLoading);
  }

  loadingRequestData(statusLoading = true) {
    this.showEventListControl = true;
    if (statusLoading) {
      this.eventList.original = null;
      this.spinnerService.start();
      this.disableTable();
    } else {
      this.spinnerService.finish();
      this.disableTable(false);
    }
  }

  resetTable(reset = true) {
    this.eventList.isUserSearching.resetTable = reset;
  }

  disableTable(disable = true) {
    this.eventList.isUserSearching.searching = disable;
    this.disableSearchBar(disable);
  }

  updateToDefaultURL(): void {
    this.eventSearchParametersStore.defaultViewUrl();
  }

  updateToFinancialURL(): void {
    this.eventSearchParametersStore.financialViewUrl();
  }

  getTokenFilterFieldTranslationKey(filterField: string) {
    switch (filterField) {
      case EventFilterType.COMPLETED.toString():
      case EventFilterType.MISSING_AGENCY.toString():
      case EventFilterType.ARCHIVED.toString():
        return 'EVENTS.FILTER_LABEL.EVENT_STATUS';
      case EventFilterType.COMM_RETENTION.toString():
          return 'EVENTS.FILTER_LABEL.EVENT_STATUS';
      case EventFilterType.DELETED.toString():
        return 'EVENTS.FILTER_LABEL.EVENT_STATUS';
      case EventFilterType.MISSING_EMAIL_CONTACT.toString():
        return '';
      default:
        return 'EVENTS.FILTER_LABEL.' + filterField;
    }
  }

  getTokenValue(filter: EventSearchFilter): string {
    return this.eventFilterUtils.getTokenDisplayString(filter);
  }

  exportEventExcel(): void {

    let filterDescriptions = Set([]);

    this.eventSearchParametersStore.eventSearchParametersSnapshot.filters.forEach((element: EventSearchFilter) => {
      filterDescriptions = filterDescriptions.add(
        this.translateService.instant(this.getTokenFilterFieldTranslationKey(element.filterField)) +
        ' ' +
        this.getTokenValue(element));
    });

    let eventSearchParamsDesc = new EventSearchParamsDesc({
      maxEvents: 0,
      searchText: this.eventSearchParametersStore.eventSearchParametersSnapshot.searchText,
      filters: this.eventSearchParametersStore.eventSearchParametersSnapshot.filters,
      sorts: this.eventSearchParametersStore.eventSearchParametersSnapshot.sorts,
      filterDescriptions: filterDescriptions
    });

    this.eventListStore.exportEventExcel(eventSearchParamsDesc).subscribe();
  }

  openBulkActionModal(action: BulkAction) {
    this.bulkActionPopup.open(action);
  }

  private disableSearchBar(isSearching) {
    if (this.eventSearchTextForm) {
      if (isSearching) {
        this.eventSearchTextForm.get('searchText').disable();
      } else {
        this.eventSearchTextForm.get('searchText').enable();
      }
    }
  }

  private cleanTable() {
    this.eventList.original = Page.empty<OnyxSummaryEvent>();
    this.eventList.filtered = Page.empty<OnyxSummaryEvent>();
    this.resetTable(false);
  }

  get searchTextSet(): Observable<Set<string>> {
    return this.eventSearchParametersStore.searchTextSet;
  }

  get searchFilterSet(): Observable<Set<EventSearchFilter>> {
    return this.eventSearchParametersStore.searchFilterSet;
  }

  get eventCount(): Observable<number> {
    return this.eventListStore.totalEventCount;
  }

  get eventsPresent(): Observable<boolean> {
    return this.eventCount.map(eventCount => {
      return eventCount > 0;
    });
  }

  get maxEvents(): number {
    return this.environment.events.maxEvents;
  }

  get events(): Observable<Page<OnyxSummaryEvent>> {
    return this.eventListStore.events;
  }

  get isDefaultViewSelected(): Observable<boolean> {
    return this.eventSearchParametersStore.isDefaultViewSelected;
  }

  get BulkAction() {
    return BulkAction;
  }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/event-list/event-list.component.ts